import { inject, Injectable, Type } from '@angular/core';
import { STORAGE_KEYS } from '../encryption/storage.types';
import { ROUTE, TeamFacadeStrategy, TeamsAdapterService } from '../../../feature-seatmap';
import { EncryptedStorageService } from '../encryption/encrypted-storage.service';
import { Observable, of, switchMap, tap } from 'rxjs';
import { TeamMapper } from '@web-portal/domains/team/anti-corruption-layer';
import { TEAM_EVENT_BUS } from '@web-portal/domains/shared/event-bus';
import { Team } from '@web-portal/domains/shared/domain-models';
import { HlmDialogService } from '@spartan-ng/helm/dialog';
import { Router } from '@angular/router';
import { CreateTeamResponse } from '../../providers/bimplus-websdk/endpoint-adapters/teams/teams.interface';

@Injectable()
export class TeamsService {
  private readonly encryptedStorage = inject(EncryptedStorageService);
  private readonly teamsAdapter = inject(TeamsAdapterService);
  private readonly teamFacade = inject(TeamFacadeStrategy);
  private readonly teamEventBus = inject(TEAM_EVENT_BUS);
  private readonly dialogService = inject(HlmDialogService);
  private readonly router = inject(Router);

  readonly sidebarTitle = 'context-nav.teams';
  readonly sidebarInlineConfig = {
    title: this.sidebarTitle,
    createLabel: this.hasOwnTeam(this.teamFacade.teams())
      ? undefined
      : 'context-nav.create-new-team',
    variant: 'inline-avatar',
    showCreateInHeader: true,
  };

  /**
   * Will retrieve the teams from the API and emit an event with the updated teams list.
   * @returns  Observable<Team[]>
   */
  getTeams(): Observable<Team[]> {
    return this.teamsAdapter.getTeams().pipe(
      tap({
        next: teams => {
          this.teamEventBus.emit({ type: '[Teams] updated', payload: teams });
        },
      })
    );
  }

  /**
   * Will first check if the team with the given ID is present in the provided teams array.
   * If it is, it will return an observable of that team.
   * If it is not, it will call the API to get the team by ID.
   * @param teamId string
   * @returns Observable<Team>
   */
  getTeamById(teamId: string): Observable<Team> {
    const cached = this.findTeamById(teamId, this.teamFacade.teams());
    if (cached) {
      return of(cached);
    }
    return this.teamsAdapter.getTeamById(teamId);
  }

  /**
   * Will emit an event with the selected team and save the selected team in the encrypted storage.
   * @param team Team
   * @returns void
   */
  selectTeam(team: Team): void {
    this.teamEventBus.emit({ type: '[Team] selected', payload: team });
    this.saveSelectedTeam(team);
  }

  /**
   * Will call the API to create a new team with the provided team data,
   * then refreshes the teams list.
   * @param teamData { name: string }
   * @returns Observable<Team>
   */
  createTeam(teamData: { name: string }): Observable<CreateTeamResponse> {
    return this.teamsAdapter
      .createTeam(teamData)
      .pipe(
        tap(team =>
          this.teamEventBus.emit({ type: '[Team] created', payload: TeamMapper.mapToTeam(team) })
        )
      );
  }

  /**
   * @returns The selected team or null if no team is selected or if the storage is not initialized.
   */
  loadSelectedTeam(): Team | null {
    if (!this.encryptedStorage.isInitialized()) {
      return null;
    }
    return this.encryptedStorage.getItem<Team>(STORAGE_KEYS.SELECTED_TEAM);
  }

  /**
   * @param team The team to save as selected. If the storage is not initialized, the method does nothing.
   */
  saveSelectedTeam(team: Team): void {
    if (!this.encryptedStorage.isInitialized()) {
      return;
    }
    this.encryptedStorage.setItemAndSave(STORAGE_KEYS.SELECTED_TEAM, team);
  }

  /**
   * @param teamId The ID of the team to find.
   * @param teams The array of teams to search in.
   * @returns The team with the given ID or undefined if no team with such ID exists in the provided teams array.
   */
  findTeamById(teamId: string, teams: Team[]): Team | undefined {
    return teams.find(t => t.id === teamId);
  }

  /**
   * @param teams The array of teams to check.
   * @returns True if the user has at least one team where isMyTeam is true, false otherwise.
   */
  hasOwnTeam(teams: { isMyTeam: boolean }[]): boolean {
    return teams.some(t => t.isMyTeam);
  }

  createTeamDialog(component: Type<unknown>, strategy: unknown): void {
    const ref = this.dialogService.open(component, { context: { strategy } });
    ref?.closed$
      .pipe(
        switchMap((result: unknown) => {
          if (!result || typeof result !== 'object' || !('name' in result)) {
            return of(null);
          }

          const name = (result as { name?: unknown }).name;
          return typeof name === 'string' && name.trim()
            ? this.createTeam({ name: name.trim() })
            : of(null);
        })
      )
      .subscribe({
        next: result => {
          if (result) {
            this.router.navigate(['/' + ROUTE.teams, result.id]);
          }
        },
      });
  }
}
